import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Star, CheckCircle } from "lucide-react";
import { getSellerLogoUrl } from "@/lib/utils/avatars";

interface SellerInfoProps {
  name: string;
  verified: boolean;
  logo: string;
  rating: string;
  reviewCount: number;
  bio: string;
  sellerId: string;
  offerCount: number;
  slug?: string;
}

const SellerInfo = ({
  name,
  verified,
  logo,
  rating,
  reviewCount,
  bio,
  sellerId,
  offerCount,
  slug
}: SellerInfoProps) => {
  // Use slug if available, otherwise use ID
  const profileUrl = slug ? `/sellers/${slug}` : `/sellers/${sellerId}`;
  
  // Get the logo URL, falling back to UI Avatars if none is provided
  const sellerLogoUrl = getSellerLogoUrl(logo, name);
  
  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="text-lg">About the Seller</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-4">
          <img 
            src={sellerLogoUrl} 
            alt={name}
            className="h-12 w-12 rounded-full"
          />
          
          <div className="space-y-1">
            <div className="flex items-center">
              <h3 className="font-semibold">{name}</h3>
              {verified && (
                <CheckCircle className="h-4 w-4 text-green-500 ml-1" />
              )}
            </div>
            
            <div className="flex items-center">
              <div className="flex items-center">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 mr-1" />
                <span>{rating}</span>
              </div>
              <span className="mx-1">•</span>
              <span>{reviewCount} reviews</span>
              {offerCount > 0 && (
                <>
                  <span className="mx-1">•</span>
                  <span>{offerCount} offers</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 line-clamp-4">{bio}</p>
        
        <div className="pt-2">
          <Link to={profileUrl}>
            <Button variant="outline" className="w-full">
              View Profile
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default SellerInfo;
